import { portalText } from "../../i18n/portal.js";
/**
 * Area `financial-document` of the console gateway — over `apps/api/src/routes/financial-document/` (design §27.9.2).
 * Owned by plan-014 unit **D5b (then D6)**.
 *
 *   listFinancialDocuments      `listFinancialDocuments`      a page of issued documents, the month in progress, and
 *                                                             whether documents are being issued or withheld
 *   downloadFinancialDocument   `downloadFinancialDocument`   a read-only link to one PDF, for 15 minutes (audited)
 *   resendFinancialDocument     `resendFinancialDocument`     email it again (a new delivery, never a new document)
 *
 * plan-014 D6 adds the organization's finance reads and exports, here because the area's slice is D5b's then D6's and
 * no new slice may be spread into the gateways (REG's hotspot):
 *
 *   readFinanceOverview         `getFinanceOverview`          every balance the person may read, by workspace — settled,
 *                                                             pending and estimated apart, with when and in which zone
 *   exportFinancialDocuments    `exportFinancialDocuments`    the documents list as one CSV, with its filters
 *   exportLedgerEntries         `exportLedgerEntries`         one balance's Transactions as one CSV
 *   readWorkspaceFunding        `getWorkspaceFunding`         which balance pays for the open workspace, since when, and
 *                                                             what the person may do with it (Billing, Transactions)
 *   readRefundable              `getRefundable`               unused purchased credit on a balance (D3b's route)
 *   requestRefund               `createRefundRequest`         a refund of unused purchased credit, for an operator (D3b)
 *
 * ── THE GENERATED CLIENT ─────────────────────────────────────────────────────────────────────────────────────
 * The operations are in D5b's fragments; the merged spec and `@wetalk/api-client`'s generated client are regenerated by
 * the api-client lock holder (plan-014 §2.3), never by a unit. So this slice reads them through {@link operationsOf}, a
 * structural view of the client with exactly these methods (D5a's pattern): it compiles before and after the
 * regeneration, and a client that still lacks one raises naming the regeneration — never an empty answer.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

export type FinancialDocumentKindView = "payment_receipt" | "invoice" | "consumption_statement" | "credit_note";
export type FinancialDocumentStateView = "awaiting_transmission" | "issued" | "corrected";
export type FinancialDocumentPaymentStateView = "not_applicable" | "unpaid" | "partially_paid" | "paid" | "credited";

export interface SupplyPeriodView {
  readonly start: string;
  readonly end: string;
  readonly timeZone: string;
}

export interface FinancialDocumentView {
  readonly financialDocumentId: string;
  readonly kind: FinancialDocumentKindView;
  readonly documentNumber: string | null;
  readonly documentState: FinancialDocumentStateView;
  readonly paymentState: FinancialDocumentPaymentStateView;
  readonly issuedAt: string;
  readonly supplyPeriod: SupplyPeriodView | null;
  readonly billingAccountName: string | null;
  readonly grossMinor: number;
  readonly currency: string;
  readonly downloadable: boolean;
}

/** The month in progress for one prepaid balance — "Not issued yet". */
export interface EstimatedDocumentView {
  readonly billingAccountId: string;
  readonly billingAccountName: string | null;
  readonly supplyPeriod: SupplyPeriodView;
  readonly netMinor: number;
  readonly currency: string;
}

export interface FinancialDocumentPageView {
  readonly document: readonly FinancialDocumentView[];
  readonly nextCursor: string | null;
  readonly estimated: readonly EstimatedDocumentView[];
  /** `withheld` while WeTalk's issuer details are not set (no document is issued; payments are still taken). */
  readonly issuance: "issuing" | "withheld";
}

export interface FinancialDocumentQuery {
  readonly kind: FinancialDocumentKindView | null;
  readonly cursor: string | null;
  /** plan-014 D6: the documents list's other filters (`period` = `YYYY-MM`, the billing month). Optional: absent is all. */
  readonly period?: string | null;
  readonly workspaceId?: string | null;
  readonly billingAccountId?: string | null;
  readonly paymentState?: FinancialDocumentPaymentStateView | null;
}

// ── plan-014 D6 views ─────────────────────────────────────────────────────────────────────────────────────

export interface FinanceOverviewWorkspaceView {
  readonly workspaceId: string;
  readonly workspaceName: string;
  readonly fundedSince: string | null;
  readonly settledMinor: number;
  readonly estimatedMinor: number;
}

export interface FinanceOverviewBalanceView {
  readonly billingAccountId: string;
  readonly displayName: string | null;
  readonly scope: "account" | "workspace";
  readonly lifecycleState: "active" | "retiring" | "retired";
  readonly collectionPolicy: "prepaid" | "postpaid";
  readonly currency: string;
  readonly settledMinor: number;
  /** plan-020 WAPP-BILL: the part of `settledMinor` that is plan fees (a plan fee names no workspace). */
  readonly subscriptionFeeMinor: number;
  readonly pendingMinor: number;
  readonly pendingCount: number;
  readonly estimatedMinor: number;
  readonly workspace: readonly FinanceOverviewWorkspaceView[];
}

/** Settled, pending and estimated kept apart, with the instant and the timezone they were worked out in. */
export interface FinanceOverviewView {
  readonly calculatedAt: string;
  readonly timeZone: string;
  readonly period: { readonly month: string; readonly start: string; readonly end: string; readonly inProgress: boolean };
  readonly reader: "organization" | "delegated";
  readonly currency: string | null;
  readonly total: { readonly settledMinor: number; readonly subscriptionFeeMinor: number; readonly pendingMinor: number; readonly estimatedMinor: number };
  readonly balance: readonly FinanceOverviewBalanceView[];
}

export interface FinanceOverviewQuery {
  readonly period: string | null;
  readonly workspaceId: string | null;
  readonly billingAccountId: string | null;
}

/** A generated CSV and its short-lived link. */
export interface FinanceExportView {
  readonly downloadUrl: string;
  readonly expiresAt: string;
  readonly rowCount: number;
}

export type FinanceAccessView = "manage" | "read" | "none";

/** Which balance pays for the open workspace (the Billing and Transactions screens' funding line). */
export interface WorkspaceFundingView {
  readonly workspaceId: string;
  readonly billingAccountId: string;
  readonly displayName: string | null;
  readonly scope: "account" | "workspace";
  readonly since: string | null;
  readonly billingTimeZone: string | null;
  readonly fundedWorkspaceCount: number;
  /** Names of the workspaces it pays for — all of them only when `fundedWorkspaceNamesComplete`. */
  readonly fundedWorkspace: readonly { readonly workspaceId: string; readonly workspaceName: string }[];
  readonly fundedWorkspaceNamesComplete: boolean;
  /** What the person may do with the balance: `none` sees spend only (FR12-DOC-05). */
  readonly financeAccess: FinanceAccessView;
}

export interface RefundableView {
  readonly billingAccountId: string;
  readonly refundableMinor: number;
  readonly availableMinor: number;
  readonly currency: string;
  readonly refundable: boolean;
  readonly rule: string;
}

export interface RefundRequestView {
  readonly operationId: string;
  readonly amountMinor: number;
  readonly currency: string;
}

export interface FinancialDocumentDownloadView {
  readonly url: string;
  readonly expiresAt: string;
  readonly fileName: string;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** A page of the organization's financial documents (newest first) and the month in progress. */
    listFinancialDocuments(accountId: string, query: FinancialDocumentQuery): Promise<FinancialDocumentPageView>;
    /** A read-only link to one document's PDF; the download is audited before the link exists. */
    downloadFinancialDocument(financialDocumentId: string): Promise<FinancialDocumentDownloadView>;
    /** Emails the document again to the billing emails in force now. */
    resendFinancialDocument(financialDocumentId: string): Promise<void>;
    /** plan-014 D6: every balance the person may read, by workspace — settled, pending and estimated apart. */
    readFinanceOverview(accountId: string, query: FinanceOverviewQuery): Promise<FinanceOverviewView>;
    /** plan-014 D6: the documents list as one CSV (audited before the rows are read). */
    exportFinancialDocuments(accountId: string, query: Omit<FinancialDocumentQuery, "cursor">): Promise<FinanceExportView>;
    /** plan-014 D6: one balance's Transactions as one CSV. */
    exportLedgerEntries(billingAccountId: string, range: { readonly from: string | null; readonly to: string | null }): Promise<FinanceExportView>;
    /** plan-014 D6: which balance pays for the open workspace, and what the person may do with it. */
    readWorkspaceFunding(): Promise<WorkspaceFundingView>;
    /** plan-014 D6 over D3b's route: unused purchased credit on a balance. */
    readRefundable(billingAccountId: string): Promise<RefundableView>;
    /** plan-014 D6 over D3b's route: asks WeTalk to refund unused purchased credit (`null` = all of it). */
    requestRefund(billingAccountId: string, amountMinor: number | null, idempotencyKey: string): Promise<RefundRequestView>;
  }
}

export type FinancialDocumentGatewaySlice = Pick<
  ConsoleGateway,
  | "listFinancialDocuments"
  | "downloadFinancialDocument"
  | "resendFinancialDocument"
  | "readFinanceOverview"
  | "exportFinancialDocuments"
  | "exportLedgerEntries"
  | "readWorkspaceFunding"
  | "readRefundable"
  | "requestRefund"
>;

/** This area's rows of `GATEWAY_GAP`: none — every operation is in D5b's fragments. */
export const financialDocumentGap: readonly GatewayGap[] = [];

// ── the client, structurally ──────────────────────────────────────────────────────────────────────────────

interface FinancialDocumentOperations {
  listFinancialDocuments(request: { path: { account_id: string }; query?: Readonly<Record<string, string>> }): Promise<ApiResult>;
  downloadFinancialDocument(request: { path: { financial_document_id: string } }): Promise<ApiResult>;
  resendFinancialDocument(request: { path: { financial_document_id: string }; body?: unknown }): Promise<ApiResult>;
  getFinanceOverview(request: { path: { account_id: string }; query?: Readonly<Record<string, string>> }): Promise<ApiResult>;
  exportFinancialDocuments(request: { path: { account_id: string }; query?: Readonly<Record<string, string>> }): Promise<ApiResult>;
  exportLedgerEntries(request: { path: { billing_account_id: string }; query?: Readonly<Record<string, string>> }): Promise<ApiResult>;
  getWorkspaceFunding(request?: Readonly<Record<string, never>>): Promise<ApiResult>;
  getRefundable(request: { path: { billing_account_id: string } }): Promise<ApiResult>;
  createRefundRequest(request: { path: { billing_account_id: string }; body: unknown; header: { "idempotency-key": string } }): Promise<ApiResult>;
}

function operationsOf(client: WeTalkApiClient): FinancialDocumentOperations {
  const held = client as unknown as Partial<Record<keyof FinancialDocumentOperations, unknown>>;
  return new Proxy({} as FinancialDocumentOperations, {
    get(_target, name: string) {
      const found = held[name as keyof FinancialDocumentOperations];
      if (typeof found !== "function") {
        throw new Error(
          portalText("shell", "the_generated_api_client_has_no_value_regenerate_it_pnpm_a_9d7a3a42", { v0: String(name) }),
        );
      }
      return (found as (...argument: unknown[]) => unknown).bind(client);
    },
  });
}

// ── decoding ──────────────────────────────────────────────────────────────────────────────────────────────

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const found = allowed.find((one) => one === value);
  if (found === undefined) throw new Error(`${path}: ${JSON.stringify(value)} is not one of ${allowed.join(", ")}`);
  return found;
}

function textOrNull(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

function integerOf(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) throw new Error(`${path} is not a whole number`);
  return value;
}

function periodOf(value: unknown, path: string): SupplyPeriodView {
  const one = readRecord(value, path);
  return { start: readText(one["start"], `${path}.start`), end: readText(one["end"], `${path}.end`), timeZone: readText(one["time_zone"], `${path}.time_zone`) };
}

const KIND: readonly FinancialDocumentKindView[] = ["payment_receipt", "invoice", "consumption_statement", "credit_note"];
const STATE: readonly FinancialDocumentStateView[] = ["awaiting_transmission", "issued", "corrected"];
const PAYMENT: readonly FinancialDocumentPaymentStateView[] = ["not_applicable", "unpaid", "partially_paid", "paid", "credited"];

export function financialDocumentOfWire(data: Readonly<Record<string, unknown>>, path: string): FinancialDocumentView {
  return {
    financialDocumentId: readText(data["financial_document_id"], `${path}.financial_document_id`),
    kind: oneOf(KIND, data["kind"], `${path}.kind`),
    documentNumber: textOrNull(data["document_number"], `${path}.document_number`),
    documentState: oneOf(STATE, data["document_state"], `${path}.document_state`),
    paymentState: oneOf(PAYMENT, data["payment_state"], `${path}.payment_state`),
    issuedAt: readText(data["issued_at"], `${path}.issued_at`),
    supplyPeriod: data["supply_period"] === null ? null : periodOf(data["supply_period"], `${path}.supply_period`),
    billingAccountName: textOrNull(data["billing_account_name"], `${path}.billing_account_name`),
    grossMinor: integerOf(data["gross_minor"], `${path}.gross_minor`),
    currency: readText(data["currency"], `${path}.currency`),
    downloadable: readBoolean(data["downloadable"], `${path}.downloadable`),
  };
}

export function financialDocumentPageOfWire(body: unknown): FinancialDocumentPageView {
  const page = readRecord(body, "listFinancialDocuments");
  const issuance = readRecord(page["issuance"], "listFinancialDocuments.issuance");
  return {
    document: readArray(page["data"], "listFinancialDocuments.data").map((one, at) =>
      financialDocumentOfWire(readRecord(one, `listFinancialDocuments.data[${String(at)}]`), `listFinancialDocuments.data[${String(at)}]`),
    ),
    nextCursor: textOrNull(page["next_cursor"], "listFinancialDocuments.next_cursor"),
    estimated: readArray(page["estimated"], "listFinancialDocuments.estimated").map((one, at) => {
      const path = `listFinancialDocuments.estimated[${String(at)}]`;
      const row = readRecord(one, path);
      return {
        billingAccountId: readText(row["billing_account_id"], `${path}.billing_account_id`),
        billingAccountName: textOrNull(row["billing_account_name"], `${path}.billing_account_name`),
        supplyPeriod: periodOf(row["supply_period"], `${path}.supply_period`),
        netMinor: readWholeNumber(row["net_minor"], `${path}.net_minor`),
        currency: readText(row["currency"], `${path}.currency`),
      };
    }),
    issuance: oneOf(["issuing", "withheld"] as const, issuance["state"], "listFinancialDocuments.issuance.state"),
  };
}

const LIFECYCLE = ["active", "retiring", "retired"] as const;
const COLLECTION = ["prepaid", "postpaid"] as const;
const SCOPE = ["account", "workspace"] as const;

export function financeOverviewOfWire(data: Readonly<Record<string, unknown>>, path: string): FinanceOverviewView {
  const period = readRecord(data["period"], `${path}.period`);
  const total = readRecord(data["total"], `${path}.total`);
  return {
    calculatedAt: readText(data["calculated_at"], `${path}.calculated_at`),
    timeZone: readText(data["time_zone"], `${path}.time_zone`),
    period: {
      month: readText(period["month"], `${path}.period.month`),
      start: readText(period["start"], `${path}.period.start`),
      end: readText(period["end"], `${path}.period.end`),
      inProgress: readBoolean(period["in_progress"], `${path}.period.in_progress`),
    },
    reader: oneOf(["organization", "delegated"] as const, data["reader"], `${path}.reader`),
    currency: textOrNull(data["currency"], `${path}.currency`),
    total: {
      settledMinor: integerOf(total["settled_minor"], `${path}.total.settled_minor`),
      subscriptionFeeMinor: integerOf(total["subscription_fee_minor"], `${path}.total.subscription_fee_minor`),
      pendingMinor: integerOf(total["pending_minor"], `${path}.total.pending_minor`),
      estimatedMinor: integerOf(total["estimated_minor"], `${path}.total.estimated_minor`),
    },
    balance: readArray(data["billing_account"], `${path}.billing_account`).map((raw, at) => {
      const where = `${path}.billing_account[${at}]`;
      const one = readRecord(raw, where);
      return {
        billingAccountId: readText(one["billing_account_id"], `${where}.billing_account_id`),
        displayName: textOrNull(one["display_name"], `${where}.display_name`),
        scope: oneOf(SCOPE, one["scope"], `${where}.scope`),
        lifecycleState: oneOf(LIFECYCLE, one["lifecycle_state"], `${where}.lifecycle_state`),
        collectionPolicy: oneOf(COLLECTION, one["collection_policy"], `${where}.collection_policy`),
        currency: readText(one["currency"], `${where}.currency`),
        settledMinor: integerOf(one["settled_minor"], `${where}.settled_minor`),
        subscriptionFeeMinor: integerOf(one["subscription_fee_minor"], `${where}.subscription_fee_minor`),
        pendingMinor: integerOf(one["pending_minor"], `${where}.pending_minor`),
        pendingCount: readWholeNumber(one["pending_count"], `${where}.pending_count`),
        estimatedMinor: integerOf(one["estimated_minor"], `${where}.estimated_minor`),
        workspace: readArray(one["workspace"], `${where}.workspace`).map((rawWorkspace, index) => {
          const inner = `${where}.workspace[${index}]`;
          const workspace = readRecord(rawWorkspace, inner);
          return {
            workspaceId: readText(workspace["workspace_id"], `${inner}.workspace_id`),
            workspaceName: readText(workspace["workspace_name"], `${inner}.workspace_name`),
            fundedSince: textOrNull(workspace["funded_since"], `${inner}.funded_since`),
            settledMinor: integerOf(workspace["settled_minor"], `${inner}.settled_minor`),
            estimatedMinor: integerOf(workspace["estimated_minor"], `${inner}.estimated_minor`),
          };
        }),
      };
    }),
  };
}

export function workspaceFundingOfWire(data: Readonly<Record<string, unknown>>, path: string): WorkspaceFundingView {
  return {
    workspaceId: readText(data["workspace_id"], `${path}.workspace_id`),
    billingAccountId: readText(data["billing_account_id"], `${path}.billing_account_id`),
    displayName: textOrNull(data["display_name"], `${path}.display_name`),
    scope: oneOf(SCOPE, data["scope"], `${path}.scope`),
    since: textOrNull(data["since"], `${path}.since`),
    billingTimeZone: textOrNull(data["billing_timezone"], `${path}.billing_timezone`),
    fundedWorkspaceCount: readWholeNumber(data["funded_workspace_count"], `${path}.funded_workspace_count`),
    fundedWorkspace: readArray(data["funded_workspace"], `${path}.funded_workspace`).map((raw, at) => {
      const one = readRecord(raw, `${path}.funded_workspace[${at}]`);
      return {
        workspaceId: readText(one["workspace_id"], `${path}.funded_workspace[${at}].workspace_id`),
        workspaceName: readText(one["workspace_name"], `${path}.funded_workspace[${at}].workspace_name`),
      };
    }),
    fundedWorkspaceNamesComplete: readBoolean(data["funded_workspace_names_complete"], `${path}.funded_workspace_names_complete`),
    financeAccess: oneOf(["manage", "read", "none"] as const, data["finance_access"], `${path}.finance_access`),
  };
}

function exportOfWire(result: ApiResult, operationId: string): FinanceExportView {
  const data = readRecord(dataOf(result, operationId), `${operationId}.data`);
  return {
    downloadUrl: readText(data["download_url"], `${operationId}.data.download_url`),
    expiresAt: readText(data["expires_at"], `${operationId}.data.expires_at`),
    rowCount: readWholeNumber(data["row_count"], `${operationId}.data.row_count`),
  };
}

/** The documents list's filters as query parameters (absent is all). */
export function documentQueryParameterOf(query: Omit<FinancialDocumentQuery, "cursor"> & { readonly cursor?: string | null }): Record<string, string> {
  const parameter: Record<string, string> = {};
  if (query.kind !== null) parameter["kind"] = query.kind;
  if (query.cursor !== undefined && query.cursor !== null) parameter["cursor"] = query.cursor;
  if (query.period !== undefined && query.period !== null) parameter["period"] = query.period;
  if (query.workspaceId !== undefined && query.workspaceId !== null) parameter["workspace_id"] = query.workspaceId;
  if (query.billingAccountId !== undefined && query.billingAccountId !== null) parameter["billing_account_id"] = query.billingAccountId;
  if (query.paymentState !== undefined && query.paymentState !== null) parameter["payment_state"] = query.paymentState;
  return parameter;
}

function dataOf(result: ApiResult, operationId: string): unknown {
  return readRecord(assertOk(result), operationId)["data"];
}

export function financialDocumentApiSlice(client: WeTalkApiClient, _context: SliceContext): FinancialDocumentGatewaySlice {
  const operations = operationsOf(client);
  return {
    async listFinancialDocuments(accountId, query) {
      return financialDocumentPageOfWire(assertOk(await operations.listFinancialDocuments({ path: { account_id: accountId }, query: documentQueryParameterOf(query) })));
    },
    async downloadFinancialDocument(financialDocumentId) {
      const data = readRecord(dataOf(await operations.downloadFinancialDocument({ path: { financial_document_id: financialDocumentId } }), "downloadFinancialDocument"), "downloadFinancialDocument.data");
      return {
        url: readText(data["url"], "downloadFinancialDocument.data.url"),
        expiresAt: readText(data["expires_at"], "downloadFinancialDocument.data.expires_at"),
        fileName: readText(data["file_name"], "downloadFinancialDocument.data.file_name"),
      };
    },
    async resendFinancialDocument(financialDocumentId) {
      assertOk(await operations.resendFinancialDocument({ path: { financial_document_id: financialDocumentId } }));
    },
    async readFinanceOverview(accountId, query) {
      const parameter: Record<string, string> = {};
      if (query.period !== null) parameter["period"] = query.period;
      if (query.workspaceId !== null) parameter["workspace_id"] = query.workspaceId;
      if (query.billingAccountId !== null) parameter["billing_account_id"] = query.billingAccountId;
      const data = readRecord(dataOf(await operations.getFinanceOverview({ path: { account_id: accountId }, query: parameter }), "getFinanceOverview"), "getFinanceOverview.data");
      return financeOverviewOfWire(data, "getFinanceOverview.data");
    },
    async exportFinancialDocuments(accountId, query) {
      return exportOfWire(await operations.exportFinancialDocuments({ path: { account_id: accountId }, query: documentQueryParameterOf(query) }), "exportFinancialDocuments");
    },
    async exportLedgerEntries(billingAccountId, range) {
      const parameter: Record<string, string> = {};
      if (range.from !== null) parameter["from"] = range.from;
      if (range.to !== null) parameter["to"] = range.to;
      return exportOfWire(await operations.exportLedgerEntries({ path: { billing_account_id: billingAccountId }, query: parameter }), "exportLedgerEntries");
    },
    async readWorkspaceFunding() {
      const data = readRecord(dataOf(await operations.getWorkspaceFunding({}), "getWorkspaceFunding"), "getWorkspaceFunding.data");
      return workspaceFundingOfWire(data, "getWorkspaceFunding.data");
    },
    async readRefundable(billingAccountId) {
      const data = readRecord(dataOf(await operations.getRefundable({ path: { billing_account_id: billingAccountId } }), "getRefundable"), "getRefundable.data");
      return {
        billingAccountId: readText(data["billing_account_id"], "getRefundable.data.billing_account_id"),
        refundableMinor: readWholeNumber(data["refundable_minor"], "getRefundable.data.refundable_minor"),
        availableMinor: integerOf(data["available_minor"], "getRefundable.data.available_minor"),
        currency: readText(data["currency"], "getRefundable.data.currency"),
        refundable: readBoolean(data["refundable"], "getRefundable.data.refundable"),
        rule: readText(data["rule"], "getRefundable.data.rule"),
      };
    },
    async requestRefund(billingAccountId, amountMinor, idempotencyKey) {
      const data = readRecord(
        dataOf(
          await operations.createRefundRequest({
            path: { billing_account_id: billingAccountId },
            body: amountMinor === null ? {} : { amount_minor: amountMinor },
            header: { "idempotency-key": idempotencyKey },
          }),
          "createRefundRequest",
        ),
        "createRefundRequest.data",
      );
      return {
        operationId: readText(data["operation_id"], "createRefundRequest.data.operation_id"),
        amountMinor: readWholeNumber(data["amount_minor"], "createRefundRequest.data.amount_minor"),
        currency: readText(data["currency"], "createRefundRequest.data.currency"),
      };
    },
  };
}
